// Pesquisa da Central de Solicitações (#6570 e #6571).
//
// Texto livre procura no título, na descrição e nos comentários (a conclusão é um
// comentário "✅ Conclusão: ..."), sem diferenciar acento nem maiúscula. Começar com
// "UC" procura pelo número da UC do jeito que o Faturamento casa as faturas
// (src/lib/ucCodigo.ts): pontos, traço, espaço e zeros na frente não contam.
//
// Sem imports de runtime: roda sob `node --test`.

export type BuscaSolicitacao =
    | { tipo: 'vazia' }
    | { tipo: 'texto'; termo: string }
    | { tipo: 'uc'; numero: string };

const semAcento = (v: string): string =>
    v.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

/** Número de UC como o Faturamento compara: só dígitos, sem zeros na frente. */
export const numeroUcNormalizado = (v: string | null | undefined): string =>
    String(v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');

export function interpretarBusca(texto: string | null | undefined): BuscaSolicitacao {
    const t = String(texto ?? '').trim();
    if (!t) return { tipo: 'vazia' };
    const uc = t.match(/^uc\s*[:#-]?\s*([\d.\-\s]+)$/i);
    if (uc) {
        const numero = numeroUcNormalizado(uc[1]);
        // Menos de 4 dígitos acharia qualquer número solto no texto.
        if (numero.length >= 4) return { tipo: 'uc', numero };
    }
    return { tipo: 'texto', termo: semAcento(t) };
}

export function textoContem(texto: string | null | undefined, termoNormalizado: string): boolean {
    return !!texto && semAcento(texto).includes(termoNormalizado);
}

/**
 * Os números de UC escritos no texto, já normalizados. Junta dígitos separados por
 * ponto, traço ou espaço ("1.500.203.012-60" vira "150020301260").
 */
export function numerosUcNoTexto(texto: string | null | undefined): string[] {
    const juntos = String(texto ?? '').replace(/(\d)[.\-\s]+(?=\d)/g, '$1');
    return (juntos.match(/\d{4,}/g) ?? []).map(n => n.replace(/^0+/, ''));
}

export function textoCitaUc(texto: string | null | undefined, numeros: readonly string[]): boolean {
    if (!texto || numeros.length === 0) return false;
    const achados = numerosUcNoTexto(texto);
    return achados.some(n => numeros.includes(n));
}

/**
 * Expressão para o banco (operador imatch do PostgREST) que acha o número nos
 * comentários escrito de qualquer jeito: zeros na frente e ponto, traço ou espaço
 * entre os dígitos. As bordas evitam achar o número dentro de outro maior.
 */
export function regexNumeroUc(numero: string): string {
    const digitos = numero.split('').join('[.\\- ]?');
    return `(^|[^0-9])0*${digitos}([^0-9]|$)`;
}
